import api from '@api';
import Dialog from '@components/layout/main/DatasetSelectorConfirmDialog';
import config from '@config';
import CollapseIcon from '@icons/collapse.svg?react';
import { getStorageItem } from '@utils';
import type { MouseEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Dataset } from 'types/schema';
import Styled from './DatasetSelector.styled';

interface Props {
  datasets: Dataset[];
  disabled?: boolean;
  selectedDataset?: Dataset | null;
  onConfirmDatasetChange: (dataset: Dataset) => void;
}

function DatasetSelector(props: Props) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDatasetMenuOpen, setIsDatasetMenuOpen] = useState(false);
  const [pendingDataset, setPendingDataset] = useState<Dataset | null>(
    props.selectedDataset || null
  );
  const datasetDropdownRef = useRef<HTMLButtonElement>(null);
  const datasetMenuRef = useRef<HTMLMenuElement>(null);
  const menuTimeoutId = useRef(-1);

  useEffect(() => {
    if (datasetDropdownRef.current && props.datasets.length > 0) {
      const { bottom } = datasetDropdownRef.current.getBoundingClientRect();

      if (bottom + config.prompt.menu.maxHeight > window.innerHeight) {
        datasetMenuRef.current.style.setProperty('bottom', 'calc(100% + 6px)');
        datasetMenuRef.current.style.setProperty('top', 'unset');
      } else {
        datasetMenuRef.current.style.setProperty('bottom', 'unset');
        datasetMenuRef.current.style.setProperty('top', 'calc(100% + 6px)');
      }
    }
  }, [props.datasets]);

  const handleDatasetDropdownClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setIsDatasetMenuOpen(!isDatasetMenuOpen);
  };

  const handleChangeDataset = useCallback(
    (dataset: Dataset) => {
      setIsDialogOpen(false);
      props.onConfirmDatasetChange?.(dataset);
    },
    [props.onConfirmDatasetChange]
  );

  const handleSelectDataset = useCallback(
    (dataset: Dataset) => (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();

      if (dataset.id === props.selectedDataset?.id) {
        return;
      }

      // Check if the user has skipped the warning
      const shouldSkipWarning = getStorageItem(
        'octopus.dataset.skip_confirmation'
      );

      if (shouldSkipWarning) {
        handleChangeDataset(dataset);
      } else {
        setPendingDataset(dataset);
        setIsDialogOpen(true);
        api.analytics.initiatedDatasetChange({
          ['dataset_before']: props.selectedDataset?.title,
          ['dataset_after']: dataset.title
        });
      }
    },
    [props.selectedDataset, handleChangeDataset]
  );

  const handleCloseDialog = useCallback(() => {
    api.analytics.selectedDataset({
      ['dataset_before']: props.selectedDataset?.title,
      ['dataset_after']: props.selectedDataset?.title
    });

    setIsDialogOpen(false);
  }, [props.selectedDataset]);

  const datasetDropdown = props.selectedDataset ? (
    <Styled.DropdownButton
      className="dataset-dropdown-button"
      data-testid="dataset-dropdown-button"
      disabled={props.disabled}
      ref={datasetDropdownRef}
      selected={isDatasetMenuOpen}
      onMouseEnter={() => window.clearTimeout(menuTimeoutId.current)}
      onMouseLeave={() => {
        menuTimeoutId.current = window.setTimeout(
          () => setIsDatasetMenuOpen(false),
          500
        );
      }}
      onClick={handleDatasetDropdownClick}>
      <Styled.DropdownButtonLabel>
        {props.selectedDataset?.title}
      </Styled.DropdownButtonLabel>
      <Styled.DropdownButtonIcon>
        <CollapseIcon />
      </Styled.DropdownButtonIcon>
    </Styled.DropdownButton>
  ) : null;

  const datasetMenu = useMemo(() => {
    const debounceMenuToggle = (isOpen: boolean) => {
      if (menuTimeoutId.current) {
        window.clearTimeout(menuTimeoutId.current);
      }

      const shouldSkipWarning = JSON.parse(
        window.localStorage.getItem('octopus.dataset.skip_confirmation')
      );

      menuTimeoutId.current = window.setTimeout(
        () => {
          if (isOpen !== isDatasetMenuOpen) {
            setIsDatasetMenuOpen(isOpen);
          }
        },
        !shouldSkipWarning && isDialogOpen ? 0 : 500
      );
    };

    return (
      <Styled.Menu
        className="dataset-menu"
        data-testid="dataset-menu"
        hidden={!isDatasetMenuOpen}
        ref={datasetMenuRef}
        onMouseEnter={() => debounceMenuToggle(true)}
        onMouseLeave={() => debounceMenuToggle(false)}>
        <Styled.List>
          {props.datasets.map((dataset) => (
            <Styled.ListItem
              key={dataset.id}
              data-testid={`dataset-menu-item-${dataset.id}`}
              selected={props.selectedDataset?.id === dataset.id}
              onClick={handleSelectDataset(dataset)}>
              <Styled.Radio
                checked={props.selectedDataset?.id === dataset.id}
              />
              <Styled.ListItemTitle
                className="line-clamp-2"
                data-testid={`dataset-menu-item-title-${dataset.id}`}>
                {dataset.title}
              </Styled.ListItemTitle>
              <Styled.ListItemSubtitle className="line-clamp-2">
                {dataset.description}
              </Styled.ListItemSubtitle>
            </Styled.ListItem>
          ))}
        </Styled.List>
      </Styled.Menu>
    );
  }, [
    isDatasetMenuOpen,
    isDialogOpen,
    props.datasets,
    props.selectedDataset,
    handleSelectDataset
  ]);

  return (
    <>
      {datasetDropdown}
      {datasetMenu}
      <Dialog
        open={isDialogOpen}
        dataset={pendingDataset}
        onChangeDataset={handleChangeDataset}
        onClose={handleCloseDialog}
      />
    </>
  );
}

export default DatasetSelector;
